import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Send, Copy, CheckCircle2, Link2Off, Smartphone, ExternalLink, Clock, BellRing } from "lucide-react";
import { toast } from "sonner";

const COPY = {
  title: { de: "Telegram Benachrichtigungen", en: "Telegram Notifications", tr: "Telegram Bildirimleri" },
  subtitle: {
    de: "Erhalte Live-Tisch-Service-Anfragen direkt aufs Handy — keine Seite mehr offen halten.",
    en: "Get live table service requests straight on your phone — no need to keep a page open.",
    tr: "Canlı masa çağrılarını doğrudan telefonuna al — sayfa açık tutmana gerek yok.",
  },
  connected: { de: "Verbunden", en: "Connected", tr: "Bağlı" },
  notConnected: { de: "Nicht verbunden", en: "Not connected", tr: "Bağlı değil" },
  generateCode: { de: "Verbindungscode erstellen", en: "Generate connection code", tr: "Bağlantı kodu oluştur" },
  step1: { de: "1. Öffne den Bot", en: "1. Open the bot", tr: "1. Botu aç" },
  step2: { de: "2. Schreibe /start mit dem Code", en: "2. Send /start with the code", tr: "2. Kod ile /start gönder" },
  step3: { de: "3. Fertig — du bist verbunden!", en: "3. Done — you're linked!", tr: "3. Tamam — bağlandın!" },
  openBot: { de: "Bot in Telegram öffnen", en: "Open bot in Telegram", tr: "Botu Telegram'da aç" },
  copyCode: { de: "Code kopieren", en: "Copy code", tr: "Kodu kopyala" },
  copied: { de: "Kopiert!", en: "Copied!", tr: "Kopyalandı!" },
  refresh: { de: "Status aktualisieren", en: "Refresh status", tr: "Durumu yenile" },
  sendTest: { de: "Test-Nachricht senden", en: "Send test message", tr: "Test mesajı gönder" },
  unlink: { de: "Verbindung trennen", en: "Unlink", tr: "Bağlantıyı kes" },
  expiresIn: { de: "Code läuft ab in", en: "Code expires in", tr: "Kodun süresi" },
  expired: { de: "Code abgelaufen — neuen erstellen", en: "Code expired — generate a new one", tr: "Kodun süresi doldu — yenisini oluştur" },
  linkedChat: { de: "Verbundener Chat", en: "Linked chat", tr: "Bağlı sohbet" },
  whatYouGet: { de: "Was du erhältst", en: "What you'll get", tr: "Ne alacaksın" },
  notifyWaiter: { de: "🔔 Kellner gerufen", en: "🔔 Waiter called", tr: "🔔 Garson çağrıldı" },
  notifyBill: { de: "💶 Rechnung angefordert", en: "💶 Bill requested", tr: "💶 Hesap istendi" },
  notifyWater: { de: "💧 Wasser/Getränk angefordert", en: "💧 Water/drink requested", tr: "💧 Su/içecek istendi" },
  notifyCustom: { de: "📩 Eigene Nachricht", en: "📩 Custom message", tr: "📩 Özel mesaj" },
  notConfigured: {
    de: "Telegram-Bot ist auf dem Server nicht konfiguriert. Bitte später erneut versuchen.",
    en: "Telegram bot is not configured on the server. Please try again later.",
    tr: "Telegram bot sunucuda yapılandırılmamış. Lütfen daha sonra tekrar deneyin.",
  },
  groupTipTitle: {
    de: "💡 Mit Gruppen-Chat statt Privat (mehrere Kellner)",
    en: "💡 Use a group chat instead (multiple waiters)",
    tr: "💡 Özel sohbet yerine grup kullan (birden fazla garson)",
  },
  groupTipBody: {
    de: "Statt nur dich selbst kannst du auch eine Telegram-Gruppe verbinden — alle Kellner erhalten dann die Benachrichtigung und können untereinander koordinieren.",
    en: "Instead of just yourself, you can connect a Telegram group — every waiter then receives the notification and the team can coordinate.",
    tr: "Sadece kendin yerine bir Telegram grubunu da bağlayabilirsin — tüm garsonlar bildirim alır ve aralarında koordine olur.",
  },
  groupStep1: { de: "Erstelle eine Telegram-Gruppe mit deinem Team", en: "Create a Telegram group with your team", tr: "Ekibinle bir Telegram grubu oluştur" },
  groupStep2: { de: "Füge @{bot} als Mitglied hinzu", en: "Add @{bot} as a member", tr: "@{bot}'u üye olarak ekle" },
  groupStep3: {
    de: 'Schicke in der Gruppe: /start@{bot} {code}',
    en: 'In the group, send: /start@{bot} {code}',
    tr: 'Grupta gönder: /start@{bot} {code}',
  },
};

function fmtTimeLeft(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return null;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function TelegramSettings() {
  const { id } = useParams();
  const { lang } = useLang();
  const tx = (k) => COPY[k]?.[lang] || COPY[k]?.de || k;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [codeData, setCodeData] = useState(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get(`/restaurants/${id}/telegram`);
      setStatus(data);
      if (data.linked) setCodeData(null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, [id]);

  // Poll status while waiting for a code to be used
  useEffect(() => {
    if (!codeData) return;
    const t = setInterval(loadStatus, 3000);
    return () => clearInterval(t);
  }, [codeData]);

  // Tick every second for countdown
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurants/${id}/telegram/link-code`);
      setCodeData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!codeData) return;
    try {
      await navigator.clipboard.writeText(`/start ${codeData.code}`);
      toast.success(tx("copied"));
    } catch {}
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurants/${id}/telegram/test`);
      if (data.ok) toast.success("✅ Telegram OK");
      else toast.error("Send failed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm(lang === "de" ? "Verbindung wirklich trennen?" : lang === "tr" ? "Bağlantı kesilsin mi?" : "Unlink?")) return;
    setBusy(true);
    try {
      await api.delete(`/restaurants/${id}/telegram`);
      setCodeData(null);
      loadStatus();
      toast.success(tx("unlink"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const countdown = codeData ? fmtTimeLeft(codeData.expires_at) : null;
  const expired = codeData && !countdown;

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6 max-w-3xl" data-testid="telegram-page">
        {/* Header */}
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2">
            <Send className="h-3 w-3" /> Telegram · Live Tisch-Service
          </div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">{tx("title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{tx("subtitle")}</p>
        </div>

        {loading ? (
          <div className="rounded-2xl border bg-card p-8 text-center text-sm text-muted-foreground">…</div>
        ) : !status?.enabled ? (
          <div className="rounded-2xl border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 p-5 text-sm text-amber-700 dark:text-amber-300">
            {tx("notConfigured")}
          </div>
        ) : status.linked ? (
          /* LINKED VIEW */
          <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-card to-card p-6 sm:p-8 space-y-6" data-testid="telegram-linked">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 grid place-items-center shrink-0">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest font-bold text-emerald-600 dark:text-emerald-400">{tx("connected")}</div>
                <div className="text-xl font-bold mt-1">{tx("linkedChat")}: <span className="text-emerald-600 dark:text-emerald-400">{status.chat_title || "—"}</span></div>
                <div className="text-xs text-muted-foreground mt-1 capitalize">{status.chat_type}</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <Button
                onClick={sendTest}
                disabled={busy}
                className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="telegram-test-btn"
              >
                <BellRing className="h-4 w-4 mr-2" />
                {tx("sendTest")}
              </Button>
              <Button
                onClick={unlink}
                disabled={busy}
                variant="outline"
                className="rounded-xl border-rose-500/40 text-rose-600 hover:bg-rose-500/10"
                data-testid="telegram-unlink-btn"
              >
                <Link2Off className="h-4 w-4 mr-2" />
                {tx("unlink")}
              </Button>
            </div>

            <div className="pt-4 border-t border-dashed">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">{tx("whatYouGet")}</div>
              <ul className="space-y-1.5 text-sm">
                <li>{tx("notifyWaiter")}</li>
                <li>{tx("notifyBill")}</li>
                <li>{tx("notifyWater")}</li>
                <li>{tx("notifyCustom")}</li>
              </ul>
            </div>
          </div>
        ) : (
          /* NOT LINKED VIEW */
          <div className="rounded-3xl border bg-card p-6 sm:p-8" data-testid="telegram-not-linked">
            {!codeData ? (
              <div className="text-center space-y-5 py-6">
                <div className="inline-flex w-16 h-16 rounded-2xl bg-purple-500/15 text-purple-600 dark:text-purple-400 items-center justify-center mx-auto">
                  <Smartphone className="h-7 w-7" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{tx("notConnected")}</div>
                  <h2 className="text-2xl font-bold mt-2">{tx("title")}</h2>
                  <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{tx("subtitle")}</p>
                </div>
                <Button
                  onClick={generate}
                  disabled={busy}
                  size="lg"
                  className="rounded-2xl bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white shadow-lg shadow-purple-600/40 h-12 px-8"
                  data-testid="telegram-generate-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {tx("generateCode")}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-purple-500/15 text-purple-600 dark:text-purple-400 grid place-items-center font-bold text-sm shrink-0">1</div>
                  <div className="flex-1">
                    <div className="font-bold text-sm">{tx("step1")}</div>
                    <a
                      href={codeData.deep_link}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#229ED9] hover:bg-[#1d8bc0] text-white font-semibold text-sm transition-colors shadow-md"
                      data-testid="telegram-open-bot"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                        <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                      </svg>
                      {tx("openBot")} @{codeData.bot_username}
                      <ExternalLink className="h-3.5 w-3.5 ml-1 opacity-70" />
                    </a>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-purple-500/15 text-purple-600 dark:text-purple-400 grid place-items-center font-bold text-sm shrink-0">2</div>
                  <div className="flex-1">
                    <div className="font-bold text-sm">{tx("step2")}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <code
                        className="px-3 py-2 rounded-xl bg-zinc-900 text-purple-300 font-mono text-sm tracking-wider border border-purple-500/30 select-all"
                        data-testid="telegram-code"
                      >
                        /start {codeData.code}
                      </code>
                      <Button onClick={copyCode} size="sm" variant="outline" className="rounded-xl" data-testid="telegram-copy-btn">
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        {tx("copyCode")}
                      </Button>
                    </div>
                    {expired ? (
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-rose-500 font-semibold">
                        <Clock className="h-3 w-3" />
                        {tx("expired")}
                      </div>
                    ) : (
                      <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {tx("expiresIn")} <span className="font-mono font-bold tabular-nums">{countdown}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-purple-500/15 text-purple-600 dark:text-purple-400 grid place-items-center font-bold text-sm shrink-0">3</div>
                  <div className="flex-1">
                    <div className="font-bold text-sm">{tx("step3")}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {lang === "de" ? "Die Seite aktualisiert sich automatisch, sobald du verbunden bist." : lang === "tr" ? "Bağlandığında sayfa otomatik yenilenir." : "This page auto-refreshes once you're connected."}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={generate} disabled={busy} variant="outline" size="sm" className="rounded-xl" data-testid="telegram-regenerate-btn">
                    {lang === "de" ? "Neuen Code" : lang === "tr" ? "Yeni kod" : "New code"}
                  </Button>
                  <Button onClick={loadStatus} variant="ghost" size="sm" className="rounded-xl">
                    {tx("refresh")}
                  </Button>
                </div>

                {/* Group chat tip */}
                <details className="mt-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 group" data-testid="telegram-group-tip">
                  <summary className="cursor-pointer list-none p-4 font-semibold text-sm flex items-center justify-between hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-2xl transition-colors">
                    <span>{tx("groupTipTitle")}</span>
                    <span className="text-xs text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="px-4 pb-4 text-sm text-muted-foreground space-y-3 leading-relaxed">
                    <p>{tx("groupTipBody")}</p>
                    <ol className="space-y-2 list-decimal list-inside marker:text-purple-500 marker:font-bold">
                      <li>{tx("groupStep1")}</li>
                      <li>{tx("groupStep2").replace("{bot}", codeData.bot_username)}</li>
                      <li>
                        <code className="px-2 py-0.5 rounded-md bg-zinc-900 text-purple-300 font-mono text-xs">
                          /start@{codeData.bot_username} {codeData.code}
                        </code>
                      </li>
                    </ol>
                  </div>
                </details>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
